import { useEditorState } from '@tiptap/react';
import { useRef, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/Input';
import { RoundIconButton } from '@/design-system-v2/round-icon-button';
import { FEATURES, useFeature } from '@/features/feature-flags';
import { collectMentions, composerEditorOf } from '@/features/mention';
import {
  filterPeople,
  reachablePeople,
  usePeopleWikiQuery,
  type WikiPerson,
} from '@/features/people-wiki';
import { useGroupsQuery } from '@/features/people-wiki/groups/groups.query';
import {
  insertPersonMention,
  mentionedPersonIds,
  removePersonMention,
} from '@/features/people-wiki/mentions';
import { afterFrames } from '@/lib/animate';
import { classList } from '@/utils/cn';

import { type HivemindGroup } from '../hivemind.types';
import {
  GroupAvatars,
  insertGroupMention,
  mentionedGroupNames,
  peopleCountLabel,
  removeGroupMention,
} from '../mentions';

// Keys the menu itself owns: navigation and dismissal. Everything else is typing,
// and must not reach the menu's typeahead, which would jump focus to an item
const MENU_OWNED_KEYS = new Set(['ArrowDown', 'ArrowUp', 'Escape', 'Tab']);

type HivemindPickerProps = {
  draftId: string;
  triggerClassName?: string;
};

// Beside the composer's + button, so asking someone is one click away
const HivemindPicker = ({ draftId, triggerClassName }: HivemindPickerProps) => {
  const hasFeature = useFeature();
  const enabled = hasFeature(FEATURES.HIVE) && hasFeature(FEATURES.PEOPLE_WIKI);
  const { data, isLoading } = usePeopleWikiQuery({ enabled });
  const { data: groups = [] } = useGroupsQuery({ enabled });
  const editor = composerEditorOf(draftId);
  const mentions =
    useEditorState({
      editor: editor ?? null,
      selector: () => (editor ? collectMentions(editor) : []),
    }) ?? [];
  const selectedIds = mentionedPersonIds(mentions);
  const selectedGroupNames = mentionedGroupNames(mentions);
  const [query, setQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  if (!enabled) return null;

  const reachable = reachablePeople(data ?? []);

  const togglePerson = (person: WikiPerson, checked: boolean) => {
    if (!editor) return;
    if (checked) insertPersonMention(editor, person);
    else removePersonMention(editor, person.id);
  };

  const toggleGroup = (group: HivemindGroup, checked: boolean) => {
    if (!editor) return;
    if (checked) insertGroupMention(editor, group);
    else removeGroupMention(editor, group.name);
  };

  const matches = filterPeople(reachable, query);
  const normalizedQuery = query.trim().toLowerCase();
  const matchingGroups = groups.filter(({ name }) =>
    name.toLowerCase().includes(normalizedQuery)
  );

  return (
    <DropdownMenu.Root
      onOpenChange={(open) => {
        if (!open) return;
        setQuery('');
        afterFrames(2, () => searchInputRef.current?.focus());
      }}
    >
      <DropdownMenu.Trigger asChild>
        <RoundIconButton
          icon={Icons.atSign}
          size="sm"
          label="Collaborate with people"
          className={classList(
            triggerClassName,
            selectedIds.length + selectedGroupNames.length > 0 &&
              'text-foreground'
          )}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        align="start"
        collisionPadding={8}
        onCloseAutoFocus={(event) => event.preventDefault()}
        className="flex max-h-[min(20rem,var(--radix-dropdown-menu-content-available-height))] w-64 flex-col overflow-y-hidden p-0"
      >
        <div className="shrink-0 border-b border-border-strong p-1">
          <Input
            ref={searchInputRef}
            variant="plain"
            inputSize="sm"
            className="px-1.5 text-sm"
            placeholder="Search people..."
            aria-label="Search people"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (!MENU_OWNED_KEYS.has(event.key)) event.stopPropagation();
            }}
          />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-1">
          {matchingGroups.length > 0 && (
            <>
              <DropdownMenu.Label className="py-1 text-xs text-foreground-muted">
                Groups
              </DropdownMenu.Label>
              {matchingGroups.map((group) => (
                <DropdownMenu.CheckboxItem
                  key={group.name}
                  checked={selectedGroupNames.includes(group.name)}
                  onCheckedChange={(checked) => toggleGroup(group, checked)}
                  onSelect={(event) => event.preventDefault()}
                >
                  <span className="flex min-w-0 flex-1 items-center gap-2 pr-5">
                    <span className="min-w-0 flex-1 truncate">
                      {group.name}
                    </span>
                    <GroupAvatars
                      groupName={group.name}
                      sizeClassName="size-5"
                    />
                    <span className="shrink-0 text-xs text-foreground-subtle">
                      {peopleCountLabel(group.members.length)}
                    </span>
                  </span>
                </DropdownMenu.CheckboxItem>
              ))}
              <DropdownMenu.Label className="py-1 text-xs text-foreground-muted">
                People
              </DropdownMenu.Label>
            </>
          )}
          {isLoading ? (
            <DropdownMenu.Item disabled>Loading people…</DropdownMenu.Item>
          ) : reachable.length === 0 ? (
            <DropdownMenu.Item disabled>No people yet</DropdownMenu.Item>
          ) : matches.length === 0 ? (
            <DropdownMenu.Item disabled>No people found</DropdownMenu.Item>
          ) : (
            matches.map((person) => (
              <DropdownMenu.CheckboxItem
                key={person.id}
                checked={selectedIds.includes(person.id)}
                onCheckedChange={(checked) => togglePerson(person, checked)}
                onSelect={(event) => event.preventDefault()}
              >
                <span className="min-w-0 flex-1 truncate pr-5">
                  {person.name}
                </span>
              </DropdownMenu.CheckboxItem>
            ))
          )}
        </div>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

export { HivemindPicker };
